import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Navbar from '../../components/Navbar/Navbar';
import { startGuessRound, verifyGuess } from '../../services/api';
import SearchPlayers from '../../components/SearchPlayers/SearchPlayers';
import './GuessPlayerPage.css';

// ── helpers ──────────────────────────────────────────────────────────────────

function formatMarketValue(v) {
  if (!v || v <= 0) return null;
  if (v >= 1_000_000) return `€${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `€${(v / 1_000).toFixed(0)}K`;
  return `€${v}`;
}

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ── sub-components ────────────────────────────────────────────────────────────

function StatBadge({ icon, label, value, color, revealed }) {
  return (
    <div className={`gp-stat-badge ${revealed ? 'gp-stat-badge--revealed' : ''}`}
      style={revealed ? { borderLeftColor: color } : {}}>
      <span className="gp-stat-icon">{icon}</span>
      <span className="gp-stat-label" style={revealed ? { color } : {}}>{label}</span>
      {revealed
        ? <span className="gp-stat-value">{value}</span>
        : <span className="gp-stat-lock">🔒</span>}
    </div>
  );
}

function LogrosTable({ logros, revealedCount, minRows = 0 }) {
  if (!logros || logros.length === 0) return null;
  const actualVisible = Math.min(logros.length, revealedCount + 1);
  const padCount = Math.max(0, minRows - actualVisible);

  return (
    <div className="gp-logros-table">
      <div className="gp-logros-header">
        <span className="gp-logros-header-icon">🏆</span>
        <span className="gp-logros-header-text">AWARDS</span>
      </div>
      <div className="gp-logros-col-head">
        <span style={{ flex: '1.3' }}>Season</span>
        <span style={{ flex: '2.7' }}>Comp.</span>
      </div>
      {logros.map((l, i) => {
        const revealed = i < revealedCount;
        if (!revealed && i > revealedCount) return null;
        const isLast = padCount === 0 && i === actualVisible - 1;
        return (
          <div
            key={i}
            className={`gp-logro-row ${revealed ? 'gp-logro-row--revealed' : ''}`}
            style={isLast ? { borderBottom: 'none' } : {}}
          >
            {revealed ? (
              <>
                <span className="gp-logro-td" style={{ flex: '1.3' }}>{l.season || '—'}</span>
                <span className="gp-logro-td gp-logro-td--comp" style={{ flex: '2.7' }}>{l.competicion}</span>
              </>
            ) : (
              <>
                <span className="gp-logro-lock" style={{ flex: '1.3' }}>?</span>
                <span className="gp-logro-lock" style={{ flex: '2.7' }}>🔒</span>
              </>
            )}
          </div>
        );
      })}
      {Array.from({ length: padCount }).map((_, i) => (
        <div
          key={`pad-${i}`}
          className="gp-logro-row"
          style={i === padCount - 1 ? { borderBottom: 'none' } : {}}
        >
          <span className="gp-logro-lock" style={{ flex: '1.3' }}>?</span>
          <span className="gp-logro-lock" style={{ flex: '2.7' }}>🔒</span>
        </div>
      ))}
    </div>
  );
}

function FichajesTable({ fichajes, revealedCount, minRows = 0 }) {
  const actualVisible = Math.min(fichajes.length, revealedCount + 1);
  const padCount = Math.max(0, minRows - actualVisible);

  return (
    <div className="gp-fichajes-table">
      <div className="gp-fichajes-header">
        <span className="gp-fichajes-header-icon">↔</span>
        <span className="gp-fichajes-header-text">TRANSFERS</span>
      </div>
      <div className="gp-fichajes-col-head">
        <span style={{ flex: '1.2' }}>Season</span>
        <span style={{ flex: '2.4' }}>Club</span>
        <span style={{ flex: '1.4', textAlign: 'right' }}>Mkt. val.</span>
      </div>
      {fichajes.map((f, i) => {
        const revealed = i < revealedCount;
        if (!revealed && i > revealedCount) return null;
        const valStr = revealed
          ? (formatMarketValue(f.valor_mercado) || '€0')
          : null;
        const isLast = padCount === 0 && i === actualVisible - 1;
        return (
          <div
            key={i}
            className={`gp-fichaje-row ${revealed ? 'gp-fichaje-row--revealed' : ''}`}
            style={isLast ? { borderBottom: 'none' } : {}}
          >
            {revealed ? (
              <>
                <span className="gp-fichaje-td" style={{ flex: '1.2' }}>{f.temporada || '—'}</span>
                <span className="gp-fichaje-td gp-fichaje-td--dest" style={{ flex: '2.4' }}>{f.club_destino}</span>
                <span className="gp-fichaje-td gp-fichaje-td--val" style={{ flex: '1.4', textAlign: 'right' }}>{valStr}</span>
              </>
            ) : (
              <>
                <span className="gp-fichaje-lock" style={{ flex: '1.2' }}>?</span>
                <span className="gp-fichaje-lock" style={{ flex: '2.4' }}>🔒</span>
                <span className="gp-fichaje-lock" style={{ flex: '1.4', textAlign: 'right' }}>?</span>
              </>
            )}
          </div>
        );
      })}
      {Array.from({ length: padCount }).map((_, i) => (
        <div
          key={`pad-${i}`}
          className="gp-fichaje-row"
          style={i === padCount - 1 ? { borderBottom: 'none' } : {}}
        >
          <span className="gp-fichaje-lock" style={{ flex: '1.2' }}>?</span>
          <span className="gp-fichaje-lock" style={{ flex: '2.4' }}>🔒</span>
          <span className="gp-fichaje-lock" style={{ flex: '1.4', textAlign: 'right' }}>?</span>
        </div>
      ))}
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────────

const GuessPlayerPage = () => {
  const [loading, setLoading] = useState(false);
  const [gameData, setGameData] = useState(null);
  const [revealStep, setRevealStep] = useState(0);
  const [status, setStatus] = useState('playing');
  const [revealPlayer, setRevealPlayer] = useState(null);
  const [wrongGuesses, setWrongGuesses] = useState([]);

  // ── fetch round ──
  const fetchRound = useCallback(async () => {
    try {
      setLoading(true);
      setGameData(null);
      setRevealStep(0);
      setStatus('playing');
      setRevealPlayer(null);
      setWrongGuesses([]);

      const data = await startGuessRound();
      if (!data?.gameId) { toast.error('Error loading game'); return; }

      setGameData({
        gameId: data.gameId,
        age: data.age ?? null,
        position: data.position || null,
        country: data.country || null,
        fichajes: data.fichajes || [],
        logros: data.logros || [],
        answer: data.answer || '',
        league: data.league || 'normal',
      });
    } catch {
      toast.error('Failed to start round');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRound(); }, [fetchRound]);

  // ── reveal sequence (same logic as mobile) ──
  const revealSequence = useMemo(() => {
    if (!gameData) return [];
    const { age, position, country, fichajes = [], logros = [] } = gameData;

    const pool = [];
    for (let i = 1; i < fichajes.length; i++) pool.push({ type: 'fichaje' });
    for (let i = 1; i < logros.length; i++) pool.push({ type: 'logro' });
    if (age !== null && age !== undefined) pool.push({ type: 'stat', statType: 'age', value: String(age) });
    if (position) pool.push({ type: 'stat', statType: 'position', value: position });
    if (country) pool.push({ type: 'stat', statType: 'country', value: country });

    return [{ type: 'initial' }, ...shuffleArray(pool)];
  }, [gameData]);

  // ── derived counts ──
  const { revealedFichajeCount, revealedLogroCount, revealedStats } = useMemo(() => {
    let fc = 0, lc = 0;
    const stats = { age: false, position: false, country: false };
    revealSequence.slice(0, revealStep + 1).forEach(step => {
      if (step.type === 'initial') { fc = 1; lc = 1; }
      else if (step.type === 'fichaje') fc++;
      else if (step.type === 'logro') lc++;
      else if (step.type === 'stat') stats[step.statType] = true;
    });
    return { revealedFichajeCount: fc, revealedLogroCount: lc, revealedStats: stats };
  }, [revealSequence, revealStep]);

  const hintsUsed = revealStep;
  const allRevealed = revealStep >= revealSequence.length - 1;

  const revealNextStep = useCallback(() => {
    setRevealStep(prev => Math.min(prev + 1, Math.max(0, revealSequence.length - 1)));
  }, [revealSequence.length]);

  // ── submit guess ──
  const submitGuess = useCallback(async (name) => {
    if (!gameData?.gameId || !name?.trim()) return;
    try {
      const resp = await verifyGuess({ id: gameData.gameId, guess: name, league: gameData.league });
      if (resp.correct) {
        setStatus('won');
        setRevealPlayer(resp.reveal || null);
        toast.success('¡Correcto!');
        setTimeout(() => {
          document.querySelector('.gp-result')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        setWrongGuesses(prev => prev.includes(name) ? prev : [...prev, name]);
        toast.warn('Incorrecto, seguí intentando');
        revealNextStep();
      }
    } catch {
      toast.error('Error verificando la respuesta');
    }
  }, [gameData, revealNextStep]);

  const onGiveUp = useCallback(async () => {
    if (!gameData?.gameId) return;
    try {
      const resp = await verifyGuess({ id: gameData.gameId, guess: '__GIVE_UP__', league: gameData.league });
      if (resp?.reveal?.nombre) {
        setStatus('gave_up');
        setRevealPlayer(resp.reveal);
        setTimeout(() => {
          document.querySelector('.gp-result')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else if (resp?.answer) {
        toast.info(`La respuesta era: ${resp.answer}`);
      }
    } catch {
      toast.error('Error de conexión');
    }
  }, [gameData]);

  // ── render ──
  if (loading) {
    return (
      <div className="guess-container">
        <Navbar />
        <div className="gp-loading">
          <div className="gp-loading-spinner" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const r2Base = 'https://images.footy-games.com.ar';
  const folderPath = 'sofascore/sofascore_images';
  const getImg = (img) => {
    if (!img) return null;
    const m = img.match(/player\/(\d+)\/image/);
    if (m) return `${r2Base}/${folderPath}/${m[1]}.webp`;
    return img;
  };

  return (
    <div className="guess-container">
      <ToastContainer position="top-right" autoClose={2500} theme="dark" />
      <Navbar />

      <div className="gp-header">
        <h1 className="gp-title">GUESS THE PLAYER</h1>
        <span className="gp-hints-badge">💡 {hintsUsed} hints used</span>
      </div>

      {gameData && status === 'playing' && (
        <>
          {/* ── Mystery avatar ── */}
          <div className="gp-mystery">
            <div className="gp-mystery-ring">
              <span className="gp-mystery-q">?</span>
            </div>
            <p className="gp-mystery-label">WHO AM I?</p>
          </div>

          {/* ── Stat badges ── */}
          <div className="gp-stats-row">
            <StatBadge
              icon="📅" label="Age"
              value={gameData.age !== null ? `${gameData.age} years old` : '—'}
              color="#a78bfa" revealed={revealedStats.age}
            />
            <StatBadge
              icon="🎯" label="Position"
              value={gameData.position || '—'}
              color="#f472b6" revealed={revealedStats.position}
            />
            <StatBadge
              icon="🌍" label="Country"
              value={gameData.country || '—'}
              color="#10b981" revealed={revealedStats.country}
            />
          </div>

          {/* ── Transfers + Awards side by side ── */}
          {(() => {
            const visibleF = Math.min(gameData.fichajes.length, revealedFichajeCount + 1);
            const visibleL = Math.min(gameData.logros.length, revealedLogroCount + 1);
            const tableRows = Math.max(visibleF, visibleL);
            return (
              <div className="gp-tables-row">
                {gameData.fichajes.length > 0 && (
                  <FichajesTable
                    fichajes={gameData.fichajes}
                    revealedCount={revealedFichajeCount}
                    minRows={tableRows}
                  />
                )}
                {gameData.logros.length > 0 && (
                  <LogrosTable
                    logros={gameData.logros}
                    revealedCount={revealedLogroCount}
                    minRows={tableRows}
                  />
                )}
              </div>
            );
          })()}

          {/* ── Search ── */}
          <div className="gp-search-card">
            <SearchPlayers
              onSelect={(p) => submitGuess(p?.nombre || '')}
              turn="X"
              player="X"
              league="normal"
            />
            {wrongGuesses.length > 0 && (
              <div className="gp-wrong-list">
                {wrongGuesses.map((g, i) => (
                  <span key={i} className="gp-wrong-chip">✕ {g}</span>
                ))}
              </div>
            )}
          </div>

          {/* ── Actions ── */}
          <div className="gp-actions">
            <button
              className={`gp-btn-reveal ${allRevealed ? 'gp-btn-reveal--disabled' : ''}`}
              onClick={revealNextStep}
              disabled={allRevealed}
            >
              💡 {allRevealed ? 'All clues revealed' : 'Reveal next clue'}
            </button>
            <button className="gp-btn-give-up" onClick={onGiveUp}>
              🏳️ Give up
            </button>
          </div>
        </>
      )}

      {/* ── Won ── */}
      {status === 'won' && revealPlayer && (
        <div className="gp-result gp-result--won">
          <span className="gp-result-emoji">🎉</span>
          <h2 className="gp-result-title" style={{ color: '#10b981' }}>You got it!</h2>
          {getImg(revealPlayer.img) && (
            <img src={getImg(revealPlayer.img)} alt={revealPlayer.nombre} className="gp-result-avatar" />
          )}
          <p className="gp-result-name">{revealPlayer.nombre}</p>
          <div className="gp-result-tags">
            {revealPlayer.pais && <span className="gp-result-tag">🌍 {revealPlayer.pais}</span>}
            {revealPlayer.posicion && (
              <span className="gp-result-tag">
                ⚽ {Array.isArray(revealPlayer.posicion) ? revealPlayer.posicion.join(' / ') : revealPlayer.posicion}
              </span>
            )}
          </div>
          <p className="gp-result-stats">Solved with {hintsUsed} hint{hintsUsed !== 1 ? 's' : ''}</p>
          <button className="gp-btn-next" onClick={fetchRound}>Next round →</button>
        </div>
      )}

      {/* ── Gave up ── */}
      {status === 'gave_up' && revealPlayer && (
        <div className="gp-result gp-result--gave-up">
          <span className="gp-result-emoji">😔</span>
          <h2 className="gp-result-title" style={{ color: '#f97316' }}>Better luck next time!</h2>
          {getImg(revealPlayer.img) && (
            <img src={getImg(revealPlayer.img)} alt={revealPlayer.nombre} className="gp-result-avatar" />
          )}
          <p className="gp-result-name">{revealPlayer.nombre}</p>
          <div className="gp-result-tags">
            {revealPlayer.pais && <span className="gp-result-tag">🌍 {revealPlayer.pais}</span>}
            {revealPlayer.posicion && (
              <span className="gp-result-tag">
                ⚽ {Array.isArray(revealPlayer.posicion) ? revealPlayer.posicion.join(' / ') : revealPlayer.posicion}
              </span>
            )}
          </div>
          <button className="gp-btn-next" onClick={fetchRound}>Try again →</button>
        </div>
      )}
    </div>
  );
};

export default GuessPlayerPage;
